import { useMemo } from "react"
import { Input, Stack, Text } from "@sembark-travel/ui-base"
import { extractPositionalVariables, extractVariables } from "./template-parser"
import {
	TWhatsAppTemplateExample,
	TWhatsAppTemplateParameterFormat,
} from "./types"
import type { TTemplateVariable } from "../../Integrations"

/**
 * Example input shown inline under a URL button. Meta requires a sample
 * value for the (single) variable in a URL button's URL at template
 * registration. Backend already auto-fills via `urlExampleForSource` /
 * `sampleValue` for fixed-source variables; the author only needs to
 * supply one when the URL contains a wildcard / unknown placeholder
 * (e.g. `https://.../{{extras_token}}`).
 */
export function UrlButtonExampleInput({
	url,
	urlSourceKey,
	example,
	wildcardPrefix,
	availableParameters,
	disabled,
	onExampleChange,
}: {
	url: string
	urlSourceKey: string | null
	example: string
	wildcardPrefix?: string | null
	availableParameters: TTemplateVariable[]
	disabled?: boolean
	onExampleChange: (next: string) => void
}) {
	const variables = useMemo(() => extractVariables(url), [url])
	const needsAuthorExample = useMemo(() => {
		if (variables.length === 0) return false
		// Bound to a `url` source field — backend pulls the example from
		// the source's `url_example`. No author input needed.
		if (urlSourceKey && urlSourceKey.trim() !== "") return false
		const fixedKeys = new Set(availableParameters.map((p) => p.key))
		// Need an example when at least one variable isn't a fixed-source
		// parameter (typically the wildcard `{{extras_<key>}}` case).
		return variables.some((v) => !fixedKeys.has(v))
	}, [variables, urlSourceKey, availableParameters])

	if (!needsAuthorExample) return null

	const variable = variables[0]
	const isMissing = !example.trim()

	return (
		<Stack gap="1" marginTop="1">
			<Text fontSize="sm" fontWeight="semibold">
				Example for <code>{`{{${variable}}}`}</code>
			</Text>
			<Input
				value={example}
				onChange={(event) => onExampleChange(event.currentTarget.value)}
				placeholder={
					wildcardPrefix && variable.startsWith(wildcardPrefix)
						? "e.g. abc123"
						: "Sample value Meta sees at registration"
				}
				disabled={disabled}
				hasError={isMissing}
			/>
		</Stack>
	)
}

export type TExampleBlock = "body" | "header" | "footer"

/**
 * Renders example-value inputs inline beneath a text-bearing block.
 *
 * For NAMED templates: lists each unique `param_name` extracted from the
 * block's text and lets the author fill `example` per variable. Example
 * values for fixed-source variables auto-fill from `availableParameters`
 * (`sample_value`); the author only edits wildcard / unknown ones.
 *
 * For POSITIONAL templates: lists each unique index ({{1}}, {{2}}, ...)
 * and lets the author fill the value. Body wraps the row in an outer
 * array per Meta; header / footer use a flat row.
 *
 * The component reads / writes the Meta-native `TWhatsAppTemplateExample` shape that
 * lives on `body.example` / `header.example` / `footer.example`.
 */
export function ExampleInputsBlock({
	block,
	text,
	example,
	parameterFormat,
	wildcardPrefix,
	availableParameters,
	disabled,
	onExampleChange,
}: {
	block: TExampleBlock
	text: string
	example?: TWhatsAppTemplateExample
	parameterFormat: TWhatsAppTemplateParameterFormat
	wildcardPrefix?: string | null
	availableParameters: TTemplateVariable[]
	disabled?: boolean
	onExampleChange: (next: TWhatsAppTemplateExample | undefined) => void
}) {
	const isPositional = parameterFormat === "POSITIONAL"

	const namedShapeKey = `${block}_text_named_params` as const
	const positionalShapeKey = `${block}_text` as const

	const allVariables = useMemo(
		() =>
			isPositional ? extractPositionalVariables(text) : extractVariables(text),
		[text, isPositional]
	)

	// Only collect examples for variables where the backend can't auto-
	// fill a sample value. POSITIONAL indices have no source mapping at
	// all → always need a value. NAMED variables that match a fixed
	// `availableParameters` entry get their example from the source's
	// `sample_value` registry; the author only fills wildcard / unknown
	// keys (e.g. `extras_<key>`).
	const variables = useMemo(() => {
		if (isPositional) return allVariables
		const fixedKeys = new Set(availableParameters.map((p) => p.key))
		return allVariables.filter((v) => !fixedKeys.has(v))
	}, [allVariables, availableParameters, isPositional])

	// NAMED: build lookup `param_name => example` from existing example.
	const namedMap = useMemo(() => {
		const map: Record<string, string> = {}
		const entries = (example?.[namedShapeKey] || []) as Array<{
			param_name: string
			example: string
		}>
		for (const entry of entries) {
			if (entry?.param_name) {
				map[entry.param_name] = entry.example || ""
			}
		}
		return map
	}, [example, namedShapeKey])

	// POSITIONAL: existing row from the right shape (body wraps in [[...]]).
	const positionalRow = useMemo(() => {
		const raw = example?.[positionalShapeKey]
		if (!raw) return [] as string[]
		if (block === "body") {
			return Array.isArray(raw[0]) ? (raw[0] as string[]) : []
		}
		return Array.isArray(raw) ? (raw as string[]) : []
	}, [example, positionalShapeKey, block])

	// Hooks above must run on every render — early return only after them.
	if (variables.length === 0) return null

	// Auto-fill suggestion from `availableParameters` (sample_value) for
	// NAMED variables that are fixed-source. Used as placeholder text only;
	// backend also falls back to sampleValue when example is empty.
	const sampleValueFor = (variable: string): string | undefined => {
		const def = availableParameters.find((p) => p.key === variable)
		return def?.sample_value || undefined
	}

	const writeNamed = (variable: string, nextValue: string) => {
		const nextMap: Record<string, string> = { ...namedMap }
		if (nextValue === "") {
			delete nextMap[variable]
		} else {
			nextMap[variable] = nextValue
		}
		const nextEntries = variables
			.filter((v) => nextMap[v] !== undefined && nextMap[v] !== "")
			.map((v) => ({ param_name: v, example: nextMap[v] }))
		const nextExample: TWhatsAppTemplateExample = { ...(example || {}) }
		if (nextEntries.length === 0) {
			delete nextExample[namedShapeKey]
		} else {
			nextExample[namedShapeKey] = nextEntries
		}
		onExampleChange(
			Object.keys(nextExample).length === 0 ? undefined : nextExample
		)
	}

	const writePositional = (index: number, nextValue: string) => {
		const nextRow = [...positionalRow]
		while (nextRow.length < variables.length) nextRow.push("")
		nextRow[index] = nextValue
		const nextExample: TWhatsAppTemplateExample = { ...(example || {}) }
		const trimmed = nextRow.slice(0, variables.length)
		const allEmpty = trimmed.every((v) => !v)
		if (allEmpty) {
			delete nextExample[positionalShapeKey]
		} else if (block === "body") {
			;(nextExample as { body_text?: string[][] }).body_text = [trimmed]
		} else if (block === "header") {
			;(nextExample as { header_text?: string[] }).header_text = trimmed
		} else {
			;(nextExample as { footer_text?: string[] }).footer_text = trimmed
		}
		onExampleChange(
			Object.keys(nextExample).length === 0 ? undefined : nextExample
		)
	}

	return (
		<Stack gap="2" marginTop="2">
			<Stack gap="0">
				<Text fontSize="sm" fontWeight="semibold">
					Example values
				</Text>
				<Text fontSize="sm" color="muted">
					Meta requires a sample value for every variable at template
					registration.
					{wildcardPrefix
						? ` Custom variables (${wildcardPrefix}<your_key>) won't have a default.`
						: ""}
				</Text>
			</Stack>
			<Stack gap="2">
				{variables.map((variable, index) => {
					const placeholder = isPositional
						? "e.g. Alice"
						: sampleValueFor(variable) || "e.g. Alice"
					const currentValue = isPositional
						? positionalRow[index] || ""
						: namedMap[variable] || ""
					const isMissing = !currentValue.trim()
					return (
						<Stack key={variable} gap="1">
							<Text fontSize="sm" fontWeight="semibold">
								<code>{`{{${variable}}}`}</code>
							</Text>
							<Input
								value={currentValue}
								onChange={(event) =>
									isPositional
										? writePositional(index, event.currentTarget.value)
										: writeNamed(variable, event.currentTarget.value)
								}
								placeholder={placeholder}
								disabled={disabled}
								hasError={isMissing}
							/>
						</Stack>
					)
				})}
			</Stack>
		</Stack>
	)
}
